import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authFetch } from '../functions/authFetch';
import { tierLabel } from '../functions/clusterSwitch';
import config from '../Config';
import './pages.css';

const BASE = () => `${config.MASTER_API}/v1`;

const STATUS_COLORS = {
  pending:           '#f59e0b',
  approved:          '#3b82f6',
  building:          '#8b5cf6',
  validating:        '#06b6d4',
  active:            '#22c55e',
  past_due:          '#f97316',
  cancelled:         '#6b7280',
  build_failed:      '#ef4444',
  failed:            '#ef4444',
  deleting:          '#f85149',
  recovering:        '#f59e0b',
  deactivating:      '#f97316',
  deactivate:        '#f97316',
  deactivated:       '#6b7280',
  activating:        '#8b5cf6',
  activate:          '#8b5cf6',
  activate_failed:   '#ef4444',
  deactivate_failed: '#ef4444',
  deactivate_failed_reactivate_pending: '#f59e0b',
};

// Friendly labels for statuses whose raw value wouldn't read well when title-cased.
const STATUS_LABELS = {
  deactivate_failed_reactivate_pending: 'Reactivation Pending — Deactivate Failed',
};

// Statuses that indicate the cluster is in the middle of an operation.
const TRANSITIONAL_PREFIXES = ['building', 'deleting', 'recovering', 'validating', 'deactivating', 'activating'];

function statusColor(status) {
  if (STATUS_COLORS[status]) return STATUS_COLORS[status];
  const prefix = (status || '').split(' - ')[0];
  return STATUS_COLORS[prefix] || '#484f58';
}

function statusLabel(status) {
  if (!status) return '';
  if (STATUS_LABELS[status]) return STATUS_LABELS[status];
  // "building - infrastructure" → "Building — Infrastructure"
  return status.split(' - ').map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(' — ');
}

function getDisplayName(cluster) {
  if (cluster.display_name) return cluster.display_name;
  const raw = cluster.cluster_prefix || cluster.cluster_name || '';
  // Strip leading numeric account-ID prefix: "8008151413-test-prd" → "test-prd"
  return raw.replace(/^\d+-/, '');
}

function StatusDot({ status }) {
  return (
    <span
      className={`cl-status-dot${status === 'active' ? ' cl-status-dot--active' : ''}`}
      style={{ background: statusColor(status) }}
      title={statusLabel(status)}
    />
  );
}

export default function ClusterManagement() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [clusters, setClusters]           = useState([]);
  const [listLoading, setListLoading]     = useState(true);
  const [selected, setSelected]           = useState(null);
  const [pricing, setPricing]             = useState(null);
  // delete confirm state
  const [deleteTarget, setDeleteTarget]   = useState(null);  // cluster_name to delete
  const [deleteBusy, setDeleteBusy]       = useState(false);
  const [deleteError, setDeleteError]     = useState('');
  const [deletePreview, setDeletePreview] = useState(null);  // {credit_cents, days_remaining, has_subscription}
  const [deletePreviewLoading, setDeletePreviewLoading] = useState(false);

  // recover modal state
  const [recoverTarget, setRecoverTarget]         = useState(null);
  const [recoverSummary, setRecoverSummary]       = useState(null);   // { deployments, configmaps }
  const [recoverSummaryLoading, setRecoverSummaryLoading] = useState(false);
  const [recoverBusy, setRecoverBusy]             = useState(false);
  const [recoverError, setRecoverError]           = useState('');
  const [recoverLaunched, setRecoverLaunched]     = useState(false);

  // build/destroy log state
  const [buildLogs, setBuildLogs]         = useState([]);
  const [logsRefreshing, setLogsRefreshing] = useState(false);
  const manualRefreshLogsRef              = useRef(null);

  // wizard state
  const [wizardOpen, setWizardOpen]       = useState(false);
  const [wName, setWName]                 = useState('');
  const [wTier, setWTier]                 = useState('dev');
  const [wizardStep, setWizardStep]       = useState(1); // 1=config 2=checkout
  const [wizardBusy, setWizardBusy]       = useState(false);
  const [wizardError, setWizardError]     = useState('');
  const [pendingCluster, setPendingCluster] = useState(null);
  const [promoCode, setPromoCode]         = useState('');
  const [promoBusy, setPromoBusy]         = useState(false);
  const [promoError, setPromoError]       = useState('');
  const [promoApplied, setPromoApplied]   = useState(null); // {discount_dollars, discounted_price}

  const fetchClusters = useCallback(async () => {
    setListLoading(true);
    try {
      const res = await authFetch(`${BASE()}/cluster/list`, { method: 'GET' });
      if (res.status === 401) { navigate('/login'); return; }
      if (res.ok) setClusters(await res.json());
    } catch { }
    finally { setListLoading(false); }
  }, [navigate]);

  // Silent refresh — updates cluster list without showing the loading spinner
  const refreshClusters = useCallback(async () => {
    try {
      const res = await authFetch(`${BASE()}/cluster/list`, { method: 'GET' });
      if (res.ok) setClusters(await res.json());
    } catch { }
  }, []);

  const fetchPricing = useCallback(async () => {
    try {
      const res = await fetch(`${BASE()}/billing/pricing`);
      if (res.ok) setPricing(await res.json());
    } catch { }
  }, []);

  useEffect(() => {
    fetchClusters();
    fetchPricing();
  }, [fetchClusters, fetchPricing]);

  // Auto-refresh every 60s while any cluster is in a transitional state.
  useEffect(() => {
    const hasTransitional = clusters.some(c =>
      TRANSITIONAL_PREFIXES.some(p => (c.status || '').startsWith(p))
    );
    if (!hasTransitional) return;
    const id = setInterval(() => {
      authFetch(`${BASE()}/cluster/list`, { method: 'GET' })
        .then(res => res.ok ? res.json() : null)
        .then(data => { if (data) setClusters(data); })
        .catch(() => {});
    }, 60_000);
    return () => clearInterval(id);
  }, [clusters, navigate]);

  // Fetch logs for all statuses except active and deactivated
  useEffect(() => {
    if (!selected) { setBuildLogs([]); return; }
    const cluster = clusters.find(c => c.cluster_name === selected);
    const st = cluster?.status || '';
    const noLog = st === 'active' || st === 'deactivated';
    if (noLog) { setBuildLogs([]); return; }

    const isTransitional = TRANSITIONAL_PREFIXES.some(p => st.startsWith(p));

    const fetchLogs = (manual = false) => {
      if (manual) setLogsRefreshing(true);
      authFetch(`${BASE()}/cluster/logs?cluster_name=${encodeURIComponent(selected)}`)
        .then(res => res.ok ? res.json() : null)
        .then(data => {
          if (data?.logs) {
            const lines = data.logs.split('\n').filter(Boolean);
            setBuildLogs(lines);
          }
        })
        .catch(() => {})
        .finally(() => { if (manual) setLogsRefreshing(false); });
    };

    manualRefreshLogsRef.current = isTransitional ? () => fetchLogs(true) : null;
    fetchLogs();
    // Only poll while actively in progress; static statuses fetch once
    if (!isTransitional) return;
    const id = setInterval(fetchLogs, 10_000);
    return () => { clearInterval(id); manualRefreshLogsRef.current = null; };
  }, [selected, clusters]);

  // Handle Stripe redirect back
  useEffect(() => {
    const status = searchParams.get('status');
    const clusterName = searchParams.get('cluster_name');
    if (status === 'success' && clusterName) {
      fetchClusters();
    }
  }, [searchParams, fetchClusters]);

  // Fetch resources whenever the selected cluster changes
  useEffect(() => {
    if (!selected) { setResources(null); return; }
    setResources(null);
    authFetch(`${BASE()}/cluster/resources?cluster_name=${encodeURIComponent(selected)}`)
      .then(res => res.ok ? res.json() : null)
      .then(data => { if (data) setResources(data); })
      .catch(() => {});
  }, [selected]);

  // ── wizard ────────────────────────────────────────────────────────────────

  const openWizard = () => {
    setWizardOpen(true);
    setWizardStep(1);
    setWName('');
    setWTier('dev');
    setWizardError('');
    setPendingCluster(null);
    setPromoCode('');
    setPromoError('');
    setPromoApplied(null);
  };

  const applyPromo = async () => {
    const code = promoCode.trim();
    if (!code) { setPromoError('Enter a promo code.'); return; }
    setPromoBusy(true);
    setPromoError('');
    try {
      const res = await authFetch(
        `${BASE()}/billing/promo/validate?code=${encodeURIComponent(code)}&tier=${wTier}`,
        { method: 'POST' }
      );
      if (res.status === 401) { navigate('/login'); return; }
      if (res.ok) {
        const data = await res.json();
        setPromoApplied(data);
      } else {
        const err = await res.json().catch(() => ({}));
        setPromoApplied(null);
        setPromoError(err.detail || 'Invalid promo code.');
      }
    } catch { setPromoError('Network error.'); }
    finally { setPromoBusy(false); }
  };

  const handleRequestCluster = async () => {
    if (!wName.trim()) { setWizardError('Cluster name is required.'); return; }
    if (wName.trim().length > 9) { setWizardError('Cluster name must be 9 characters or fewer.'); return; }
    setWizardBusy(true);
    setWizardError('');
    try {
      const res = await authFetch(
        `${BASE()}/cluster/request-cluster?cluster_name=${encodeURIComponent(wName.trim())}&tier=${wTier}`,
        { method: 'POST' }
      );
      if (res.status === 401) { navigate('/login'); return; }
      if (res.ok) {
        const data = await res.json();
        setPendingCluster(data.cluster_name);
        setWizardStep(2);
        await fetchClusters();
      } else {
        const err = await res.json().catch(() => ({}));
        setWizardError(err.detail || 'Failed to request cluster.');
      }
    } catch { setWizardError('Network error.'); }
    finally { setWizardBusy(false); }
  };

  const handleCheckout = async () => {
    setWizardBusy(true);
    setWizardError('');
    try {
      const promoParam = promoApplied && promoCode.trim()
        ? `&promo_code=${encodeURIComponent(promoCode.trim())}`
        : '';
      const res = await authFetch(
        `${BASE()}/billing/checkout?cluster_name=${encodeURIComponent(pendingCluster)}&tier=${wTier}${promoParam}`,
        { method: 'POST' }
      );
      if (res.status === 401) { navigate('/login'); return; }
      if (res.ok) {
        const { checkout_url } = await res.json();
        window.location.href = checkout_url;
      } else {
        const err = await res.json().catch(() => ({}));
        setWizardError(err.detail || 'Failed to create checkout session.');
      }
    } catch { setWizardError('Network error.'); }
    finally { setWizardBusy(false); }
  };

  // ── tier price helper ─────────────────────────────────────────────────────

  const tierPrice = (tier) => {
    if (!pricing) return '…';
    const cents = pricing.pricing?.[tier];
    return cents != null ? `$${cents}/mo` : '—';
  };

  const perHourPrice = () => {
    if (!pricing) return '…';
    const ph = pricing.deploymentUsagePrice?.per_hour;
    return ph != null ? `$${ph}/replica/hr` : '—';
  };

  const [startBusy, setStartBusy]   = useState(false);
  const [startError, setStartError] = useState('');
  const [toast, setToast]           = useState(null); // {message, type: 'success'|'error'}

  // ── payment method (Stripe portal) ───────────────────────────────────────
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalError, setPortalError]     = useState('');

  const [reactivateBusy, setReactivateBusy]   = useState(false);
  const [reactivateError, setReactivateError] = useState('');

  // ── cluster resources ─────────────────────────────────────────────────────
  const [resources, setResources]         = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const handleStartCluster = async (clusterName) => {
    setStartBusy(true);
    setStartError('');
    try {
      const res = await authFetch(
        `${BASE()}/cluster/create-cluster?cluster_name=${encodeURIComponent(clusterName)}`,
        { method: 'POST' }
      );
      if (res.status === 401) { navigate('/login'); return; }
      if (res.ok) {
        showToast('Build submitted — cluster is now building.');
        await refreshClusters();
      } else {
        const err = await res.json().catch(() => ({}));
        setStartError(err.detail || 'Failed to start cluster.');
      }
    } catch { setStartError('Network error.'); }
    finally { setStartBusy(false); }
  };

  const handleOpenPortal = async () => {
    setPortalLoading(true);
    setPortalError('');
    try {
      const res = await authFetch(`${BASE()}/billing/portal`, { method: 'POST' });
      if (res.status === 401) { navigate('/login'); return; }
      if (!res.ok) { setPortalError('Could not open billing portal. Please try again.'); return; }
      const { url } = await res.json();
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch { setPortalError('Network error.'); }
    finally { setPortalLoading(false); }
  };

  const handleReactivate = async (clusterName) => {
    setReactivateBusy(true);
    setReactivateError('');
    try {
      const res = await authFetch(`${BASE()}/billing/reactivate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cluster_name: clusterName }),
      });
      if (res.status === 401) { navigate('/login'); return; }
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        setReactivateError(d.detail || 'Could not process payment. Please try again.');
        return;
      }
      // Charge succeeded — reactivation (or activation) triggered server-side.
      await fetchClusters();
      setTimeout(fetchClusters, 1500);
      setTimeout(fetchClusters, 5000);
    } catch { setReactivateError('Network error.'); }
    finally { setReactivateBusy(false); }
  };

  const openRecoverModal = async (clusterName) => {
    setRecoverTarget(clusterName);
    setRecoverSummary(null);
    setRecoverError('');
    setRecoverLaunched(false);
    setRecoverSummaryLoading(true);
    try {
      const res = await authFetch(
        `${BASE()}/cluster/recovery-summary?cluster_name=${encodeURIComponent(clusterName)}`,
        { method: 'GET' }
      );
      if (res.status === 401) { navigate('/login'); return; }
      if (res.ok) setRecoverSummary(await res.json());
      else setRecoverError('Could not load recovery summary.');
    } catch { setRecoverError('Network error.'); }
    finally { setRecoverSummaryLoading(false); }
  };

  const handleStartRecover = async () => {
    if (!recoverTarget) return;
    setRecoverBusy(true);
    setRecoverError('');
    try {
      const res = await authFetch(
        `${BASE()}/cluster/restart?cluster_name=${encodeURIComponent(recoverTarget)}`,
        { method: 'POST' }
      );
      if (res.status === 401) { navigate('/login'); return; }
      if (res.ok) {
        setRecoverLaunched(true);
        await fetchClusters();
        setTimeout(fetchClusters, 1500);
        setTimeout(fetchClusters, 5000);
      } else {
        const err = await res.json().catch(() => ({}));
        setRecoverError(err.detail || 'Failed to start recovery.');
      }
    } catch { setRecoverError('Network error.'); }
    finally { setRecoverBusy(false); }
  };

  const openDeleteModal = async (clusterName) => {
    setDeleteTarget(clusterName);
    setDeleteError('');
    setDeletePreview(null);
    setDeletePreviewLoading(true);
    try {
      const res = await authFetch(
        `${BASE()}/cluster/delete-preview?cluster_name=${encodeURIComponent(clusterName)}`,
        { method: 'GET' }
      );
      if (res.ok) setDeletePreview(await res.json());
    } catch { }
    finally { setDeletePreviewLoading(false); }
  };

  const closeRecoverModal = () => {
    setRecoverTarget(null);
    setRecoverSummary(null);
    setRecoverError('');
    setRecoverLaunched(false);
  };

  const handleDeleteCluster = async () => {
    if (!deleteTarget) return;
    setDeleteBusy(true);
    setDeleteError('');
    try {
      const res = await authFetch(
        `${BASE()}/cluster/delete?cluster_name=${encodeURIComponent(deleteTarget)}`,
        { method: 'DELETE' }
      );
      if (res.status === 401) { navigate('/login'); return; }
      if (res.ok) {
        setDeleteTarget(null);
        setSelected(null);
        setDeletePreview(null);
        // Immediate refresh, then a few quick follow-ups while teardown begins
        await fetchClusters();
        setTimeout(fetchClusters, 1500);
        setTimeout(fetchClusters, 5000);
      } else {
        const err = await res.json().catch(() => ({}));
        setDeleteError(err.detail || 'Failed to delete cluster.');
      }
    } catch { setDeleteError('Network error.'); }
    finally { setDeleteBusy(false); }
  };

  // ── selected cluster detail ───────────────────────────────────────────────

  const selectedCluster = clusters.find(c => c.cluster_name === selected);

  const formatDate = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }); }
    catch { return iso; }
  };

  const usagePct = (usage) => {
    const u = parseFloat(usage) || 0;
    return Math.min(100, (u / 100) * 100).toFixed(0);
  };

  const graceDaysLeft = (pastDueSinceIso) => {
    if (!pastDueSinceIso) return null;
    const since = new Date(pastDueSinceIso.endsWith('Z') ? pastDueSinceIso : pastDueSinceIso + 'Z');
    const elapsedDays = (Date.now() - since.getTime()) / 86_400_000;
    return Math.max(0, Math.ceil(3 - elapsedDays));
  };

  // Days remaining before a deactivated cluster's data is permanently deleted.
  // Uses deactivated_at if available; falls back to past_due_since + 10 days
  // (3-day grace + 7-day retention) when the field wasn't recorded (manual test docs).
  const retentionDaysLeft = (deactivatedAtIso, pastDueSinceIso) => {
    const _parse = iso => new Date(iso.endsWith('Z') ? iso : iso + 'Z');
    if (deactivatedAtIso) {
      const elapsed = (Date.now() - _parse(deactivatedAtIso).getTime()) / 86_400_000;
      return Math.max(0, Math.ceil(7 - elapsed));
    }
    if (pastDueSinceIso) {
      // deactivation happens at day 3 of past_due, then 7-day retention = 10 days total
      const elapsed = (Date.now() - _parse(pastDueSinceIso).getTime()) / 86_400_000;
      return Math.max(0, Math.ceil(10 - elapsed));
    }
    return null;
  };

  // ── render ────────────────────────────────────────────────────────────────

  return (
    <div className="cl-page">

      {/* sidebar */}
      <div className="cl-sidebar">
        <div className="cl-sidebar-header">
          <span className="cl-sidebar-title">My Clusters</span>
          <button className="cl-new-btn" onClick={openWizard}>+ New</button>
        </div>

        {listLoading ? (
          <p className="cl-sidebar-loading">Loading…</p>
        ) : clusters.length === 0 ? (
          <p className="cl-sidebar-empty">No clusters yet.</p>
        ) : (
          <ul className="cl-sidebar-list">
            {clusters
              .map(c => (
                <li
                  key={c.cluster_name}
                  className={`cl-sidebar-item${selected === c.cluster_name ? ' cl-sidebar-item--active' : ''}${['cancelled', 'deleting'].includes(c.status) ? ' cl-sidebar-item--dim' : ''}`}
                  onClick={() => { setSelected(c.cluster_name); setWizardOpen(false); }}
                >
                  <StatusDot status={c.status} />
                  <div className="cl-sidebar-item-info">
                    <span className="cl-sidebar-item-name">{getDisplayName(c)}</span>
                    <span className="cl-sidebar-item-sub">{tierLabel(c.tier)} · {statusLabel(c.status)}</span>
                  </div>
                </li>
              ))
            }
          </ul>
        )}
      </div>

      {/* main panel */}
      <div className="cl-main">

        {!selected && !wizardOpen && (
          <div className="cl-empty-state">
            <p className="cl-empty-title">Cluster Management</p>
            <p className="cl-empty-prompt">select cluster or init new<span>_</span></p>
            <p className="cl-empty-desc">
              Each cluster is an <strong>isolated environment in your own private VPC</strong> — your nodes,
              network, and compute are never shared with other accounts.
              Select a cluster from the sidebar or click <strong>+ New</strong> to provision one.
            </p>
          </div>
        )}

        {/* cluster detail */}
        {selected && selectedCluster && !wizardOpen && (
          <div className="cl-detail">
            <div className="cl-detail-header">
              <span className="cl-detail-prompt">&gt;</span>
              <StatusDot status={selectedCluster.status} />
              <span className="cl-detail-name">{getDisplayName(selectedCluster)}</span>
              <span className={`cl-tier-badge cl-tier-badge--${selectedCluster.tier}`}>{tierLabel(selectedCluster.tier)}</span>
              {selectedCluster.status === 'active' && (
                <span style={{ fontSize: '0.65rem', color: '#3fb950', fontWeight: 700, letterSpacing: '0.12em' }}>● LIVE</span>
              )}
              <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
                {selectedCluster.status === 'active' && (
                  <button
                    className="cl-recover-btn"
                    onClick={() => openRecoverModal(selectedCluster.cluster_name)}
                  >
                    ↺ Recover
                  </button>
                )}
                {!['cancelled', 'deleting'].includes(selectedCluster.status) &&
                 !TRANSITIONAL_PREFIXES.some(p => selectedCluster.status.startsWith(p)) && (
                  <button
                    className="cl-delete-btn"
                    onClick={() => openDeleteModal(selectedCluster.cluster_name)}
                    disabled={deleteBusy}
                  >
                    ⌫ Delete
                  </button>
                )}
              </div>
            </div>

            <div className="cl-metric-grid">
              <div className="cl-metric-card">
                <span className="cl-metric-label">Status</span>
                <span className="cl-metric-val" style={{ color: statusColor(selectedCluster.status) }}>
                  {statusLabel(selectedCluster.status)}
                </span>
              </div>
              <div className="cl-metric-card">
                <span className="cl-metric-label">Created</span>
                <span className="cl-metric-val">{formatDate(selectedCluster.created_at)}</span>
              </div>
              {selectedCluster.stripe_current_period_end ? (
                <div className="cl-metric-card">
                  <span className="cl-metric-label">Next Billing</span>
                  <span className="cl-metric-val">{formatDate(selectedCluster.stripe_current_period_end)}</span>
                </div>
              ) : (
                <div className="cl-metric-card">
                  <span className="cl-metric-label">Billing</span>
                  <span className="cl-metric-val" style={{ color: '#484f58' }}>—</span>
                </div>
              )}
            </div>

            {/* build/destroy progress log */}
            {(() => {
              const st = selectedCluster.status || '';
              const noLog = st === 'active' || st === 'deactivated';
              if (noLog) return null;
              const isTransitional = TRANSITIONAL_PREFIXES.some(p => st.startsWith(p));
              const isFailed = !isTransitional;
              return (
                <div style={{ margin: '16px 0', background: '#0d1117', borderRadius: '8px', padding: '16px', border: `1px solid ${isFailed ? '#3d1f1f' : '#21262d'}` }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                    <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: statusColor(selectedCluster.status), display: 'inline-block', animation: isTransitional ? 'pulse 1.5s infinite' : 'none' }} />
                    <span style={{ fontSize: '0.8rem', color: '#8b949e', fontFamily: 'monospace', flex: 1 }}>
                      {statusLabel(selectedCluster.status)}
                    </span>
                    {isTransitional && (
                      <button
                        onClick={() => manualRefreshLogsRef.current?.()}
                        disabled={logsRefreshing}
                        style={{ background: 'none', border: '1px solid #30363d', borderRadius: '6px', color: logsRefreshing ? '#484f58' : '#8b949e', cursor: logsRefreshing ? 'default' : 'pointer', fontSize: '0.75rem', padding: '2px 8px', display: 'flex', alignItems: 'center', gap: '4px' }}
                      >
                        <span style={{ display: 'inline-block', animation: logsRefreshing ? 'spin 1s linear infinite' : 'none' }}>↻</span>
                        {logsRefreshing ? 'Refreshing...' : 'Refresh'}
                      </button>
                    )}
                  </div>
                  {buildLogs.length === 0 ? (
                    <p style={{ fontSize: '0.8rem', color: '#484f58', fontFamily: 'monospace', margin: 0 }}>Waiting for progress...</p>
                  ) : (
                    <ol style={{ margin: 0, padding: '0 0 0 20px', listStyle: 'none' }}>
                      {buildLogs.map((line, i) => {
                        const isLast = i === buildLogs.length - 1;
                        const isFailLine = line.toLowerCase().startsWith('job failed');
                        return (
                          <li key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '4px 0', fontSize: '0.82rem', fontFamily: 'monospace', color: isLast ? '#e6edf3' : '#8b949e' }}>
                            <span style={{ color: isFailLine ? '#f85149' : isLast ? '#3fb950' : '#484f58', fontSize: '0.7rem' }}>
                              {isFailLine ? '✕' : isLast ? '▶' : '✓'}
                            </span>
                            {line}
                          </li>
                        );
                      })}
                    </ol>
                  )}
                </div>
              );
            })()}

            {/* cluster resources */}
            {resources && (
              <div className="cl-resources-section">
                <div className="cl-resources-header">
                  <span className="cl-resources-title">Cluster Resources</span>
                </div>
                {resources.deployments?.length > 0 && (
                  <div className="cl-resource-group">
                    <span className="cl-resource-group-title">Deployments</span>
                    <div className="cl-resource-list">
                      {resources.deployments.map(d => (
                        <span key={d.app_name} className={`cl-resource-chip${d.is_deployed ? ' cl-resource-chip--active' : ''}`}>
                          {d.app_name}
                          {d.is_deployed && <span className="cl-resource-chip-dot" title="Deployed" />}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {resources.automations?.length > 0 && (
                  <div className="cl-resource-group">
                    <span className="cl-resource-group-title">Automations</span>
                    <div className="cl-resource-list">
                      {resources.automations.map(a => (
                        <span key={a.app_name} className="cl-resource-chip">
                          {a.app_name}
                          {a.status && <span className="cl-resource-chip-status">{a.status}</span>}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {resources.configmaps?.length > 0 && (
                  <div className="cl-resource-group">
                    <span className="cl-resource-group-title">ConfigMaps</span>
                    <div className="cl-resource-list">
                      {resources.configmaps.map(cm => (
                        <span key={cm} className="cl-resource-chip">{cm}</span>
                      ))}
                    </div>
                  </div>
                )}
                {resources.cron_jobs?.length > 0 && (
                  <div className="cl-resource-group">
                    <span className="cl-resource-group-title">Cron Jobs</span>
                    <div className="cl-resource-list">
                      {resources.cron_jobs.map(cj => (
                        <span
                          key={cj.name}
                          className={`cl-resource-chip${cj.enabled ? ' cl-resource-chip--active' : ''}`}
                          title={`${cj.schedule}${cj.image ? ` · ${cj.image}` : ''}${cj.enabled ? '' : ' · disabled'}${cj.has_error ? ' · last run failed' : ''}`}
                        >
                          {cj.name}
                          {cj.enabled && <span className="cl-resource-chip-dot" title="Enabled" />}
                          {cj.has_error && <span className="cl-resource-chip-status">error</span>}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {!resources.deployments?.length && !resources.automations?.length && !resources.configmaps?.length && !resources.cron_jobs?.length && (
                  <p className="cl-resource-empty">No resources configured yet.</p>
                )}
              </div>
            )}

            {/* billing action row */}
            {selectedCluster.stripe_current_period_end && (
              <div className="cl-billing-row">
                <span className="cl-billing-row-label">💳 Payment &amp; Billing</span>
                {portalError && <span className="cl-billing-row-err">{portalError}</span>}
                <button
                  className="cl-billing-portal-btn"
                  onClick={handleOpenPortal}
                  disabled={portalLoading}
                >
                  {portalLoading ? 'Opening…' : 'Manage in Stripe ↗'}
                </button>
              </div>
            )}

            {/* usage meter */}
            <div className="cl-usage-section">
              <div className="cl-usage-header">
                <p className="cl-usage-title">Usage Balance</p>
                <span className="cl-usage-pct">{usagePct(selectedCluster.usage)}%</span>
              </div>
              <p className="cl-usage-desc">
                Accruing at {perHourPrice()} per replica. Auto-charged at $100 increments.
              </p>
              <div className="cl-usage-bar-wrap">
                <div className="cl-usage-bar" style={{ width: `${usagePct(selectedCluster.usage)}%` }} />
              </div>
              <div className="cl-usage-labels">
                <span className="cl-usage-current">
                  ${(parseFloat(selectedCluster.usage) || 0).toFixed(4)}
                </span>
                <span className="cl-usage-max">$100.00 threshold</span>
              </div>
            </div>

            {/* past_due warning banner */}
            {selectedCluster.status === 'past_due' && (() => {
              const daysLeft = graceDaysLeft(selectedCluster.past_due_since);
              return (
                <div className="cl-pastdue-banner">
                  <div className="cl-pastdue-banner-icon">!</div>
                  <div className="cl-pastdue-banner-body">
                    <p className="cl-pastdue-banner-title">Payment failed — action required</p>
                    <p className="cl-pastdue-banner-desc">
                      {daysLeft !== null
                        ? daysLeft === 0
                          ? 'Your cluster infrastructure will be shut down today. Update your payment method immediately to prevent disruption.'
                          : `Your cluster infrastructure will be shut down in ${daysLeft} day${daysLeft === 1 ? '' : 's'} if payment is not resolved. Your settings will be retained for 7 more days after that.`
                        : 'Your payment has failed. Pay the outstanding balance on your card to restore service, or update your payment method first.'}
                    </p>
                    <div className="cl-pm-btn-row">
                      <button
                        className="cl-pm-pay-btn"
                        onClick={() => handleReactivate(selectedCluster.cluster_name)}
                        disabled={reactivateBusy}
                      >
                        {reactivateBusy ? 'Processing…' : '💳 Pay Outstanding Balance'}
                      </button>
                      <button className="cl-pm-update-btn" onClick={handleOpenPortal} disabled={portalLoading}>
                        {portalLoading ? 'Opening…' : 'Update Payment Method ↗'}
                      </button>
                    </div>
                    {reactivateError && <p className="cl-error" style={{ marginTop: '0.5rem' }}>{reactivateError}</p>}
                  </div>
                </div>
              );
            })()}

            {/* deactivated / deactivating banner */}
            {(selectedCluster.status === 'deactivated' || (selectedCluster.status || '').startsWith('deactivating')) && (() => {
              const isDeactivating = (selectedCluster.status || '').startsWith('deactivating');
              const reactivateScheduled = !!selectedCluster.reactivate_requested;
              const daysLeft = retentionDaysLeft(selectedCluster.deactivated_at, selectedCluster.past_due_since);
              return (
                <div className="cl-pastdue-banner cl-deactivated-banner">
                  <div className="cl-pastdue-banner-icon">⏸</div>
                  <div className="cl-pastdue-banner-body">
                    <p className="cl-pastdue-banner-title">
                      {isDeactivating ? 'Cluster is shutting down' : 'Cluster infrastructure shut down'}
                    </p>
                    <p className="cl-pastdue-banner-desc">
                      {isDeactivating
                        ? (reactivateScheduled
                            ? 'Payment received — your cluster will automatically rebuild as soon as the shutdown finishes. No further action needed.'
                            : 'Your cluster is being shut down due to a failed payment. You can pay now to have it automatically rebuilt as soon as the shutdown completes.')
                        : daysLeft === null
                          ? 'Your cluster infrastructure has been shut down due to a failed payment. Update your payment method to reactivate immediately.'
                          : daysLeft > 0
                            ? `Your cluster infrastructure has been shut down due to a failed payment. Your settings and configurations are retained for ${daysLeft} more day${daysLeft === 1 ? '' : 's'}. Update your payment method to reactivate immediately.`
                            : 'Your cluster settings are scheduled for permanent removal. Pay the outstanding balance now to attempt recovery, or update your payment method first.'}
                    </p>
                    {reactivateScheduled ? (
                      <p className="cl-pastdue-banner-desc" style={{ color: '#22c55e', fontWeight: 600 }}>
                        ✓ Reactivation scheduled
                      </p>
                    ) : (
                      <div className="cl-pm-btn-row">
                        <button
                          className="cl-pm-pay-btn"
                          onClick={() => handleReactivate(selectedCluster.cluster_name)}
                          disabled={reactivateBusy}
                        >
                          {reactivateBusy ? 'Processing…' : '💳 Pay & Reactivate'}
                        </button>
                        <button className="cl-pm-update-btn" onClick={handleOpenPortal} disabled={portalLoading}>
                          {portalLoading ? 'Opening…' : 'Update Payment Method ↗'}
                        </button>
                      </div>
                    )}
                    {reactivateError && <p className="cl-error" style={{ marginTop: '0.5rem' }}>{reactivateError}</p>}
                  </div>
                </div>
              );
            })()}

            {selectedCluster.status === 'pending' && (
              <div className="cl-action-bar">
                <p className="cl-action-hint">// payment required to begin provisioning</p>
                <button
                  className="cl-primary-btn"
                  onClick={() => {
                    setPendingCluster(selectedCluster.cluster_name);
                    setWTier(selectedCluster.tier);
                    setWizardOpen(true);
                    setWizardStep(2);
                    setWizardError('');
                  }}
                >
                  → Complete Payment
                </button>
              </div>
            )}

            {selectedCluster.status === 'approved' && (
              <div className="cl-action-bar">
                <p className="cl-action-hint">// payment confirmed — ready to provision</p>
                {startError && <p className="cl-error">{startError}</p>}
                <button
                  className="cl-primary-btn"
                  onClick={() => handleStartCluster(selectedCluster.cluster_name)}
                  disabled={startBusy}
                >
                  {startBusy ? 'Starting…' : '▶ Start Cluster'}
                </button>
              </div>
            )}

            {selectedCluster.status === 'build_failed' && (
              <div className="cl-action-bar">
                <p className="cl-action-hint" style={{ color: '#f85149' }}>// build failed — this is usually a temporary cloud provisioning hiccup</p>
                <p className="cl-action-hint">Click <strong>Retry Build</strong> — it almost always succeeds on the next attempt without recreating the cluster. If it keeps failing, delete the cluster or contact support.</p>
                {startError && <p className="cl-error">{startError}</p>}
                <button
                  className="cl-primary-btn"
                  onClick={() => handleStartCluster(selectedCluster.cluster_name)}
                  disabled={startBusy}
                >
                  {startBusy ? 'Starting…' : '↻ Retry Build'}
                </button>
              </div>
            )}

            {selectedCluster.status === 'activate_failed' && (
              <div className="cl-action-bar">
                <p className="cl-action-hint" style={{ color: '#f85149' }}>// reactivation failed — retry or contact support</p>
                {startError && <p className="cl-error">{startError}</p>}
                <button
                  className="cl-primary-btn"
                  onClick={async () => {
                    setStartBusy(true);
                    setStartError('');
                    try {
                      const res = await authFetch(
                        `${BASE()}/cluster/activate?cluster_name=${encodeURIComponent(selectedCluster.cluster_name)}`,
                        { method: 'POST' }
                      );
                      if (res.status === 401) { navigate('/login'); return; }
                      if (!res.ok) { const d = await res.json().catch(() => ({})); setStartError(d.detail || 'Failed to trigger activation.'); return; }
                      await fetchClusters();
                    } catch { setStartError('Network error.'); }
                    finally { setStartBusy(false); }
                  }}
                  disabled={startBusy}
                >
                  {startBusy ? 'Starting…' : '↻ Retry Activation'}
                </button>
              </div>
            )}

            {selectedCluster.status === 'deactivate_failed_reactivate_pending' && (
              <div className="cl-action-bar">
                <p className="cl-action-hint" style={{ color: '#f59e0b' }}>
                  // payment received, but shutdown didn't finish cleanly — recover to complete reactivation
                </p>
                <p className="cl-action-hint">
                  Your payment was accepted. The shutdown was interrupted before your cluster could be rebuilt.
                  Click Recover to run a fresh job that finishes tearing down the old infrastructure and then
                  rebuilds and restores your cluster.
                </p>
                <button
                  className="cl-primary-btn"
                  onClick={() => openRecoverModal(selectedCluster.cluster_name)}
                >
                  ↺ Recover Cluster
                </button>
              </div>
            )}
          </div>
        )}

        {/* wizard */}
        {wizardOpen && (
          <div className="cl-wizard">
            <div className="cl-wizard-header">
              <span className="cl-wizard-title">
                {wizardStep === 1 ? 'init cluster' : 'billing setup'}
              </span>
              <button className="cl-wizard-close" onClick={() => setWizardOpen(false)}>[esc]</button>
            </div>

            {wizardStep === 1 && (
              <div className="cl-wizard-body">
                <div className="cl-field">
                  <label className="cl-label">Cluster Name</label>
                  <input
                    className="cm-input cl-input"
                    placeholder="e.g. my-app"
                    value={wName}
                    onChange={e => setWName(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '').slice(0, 9))}
                    maxLength={9}
                    disabled={wizardBusy}
                  />
                  <span className="cl-hint">Lowercase letters, numbers, hyphens only. Max 9 characters.</span>
                </div>

                <div className="cl-field">
                  <label className="cl-label">Tier</label>
                  <div className="cl-tier-cards">
                    {['dev', 'prod'].map(t => (
                      <div
                        key={t}
                        className={`cl-tier-card${wTier === t ? ' cl-tier-card--selected' : ''}`}
                        onClick={() => setWTier(t)}
                      >
                        <span className="cl-tier-card-name">{tierLabel(t)}</span>
                        <span className="cl-tier-card-price">{tierPrice(t)}</span>
                        <span className="cl-tier-card-desc">
                          {t === 'dev'
                            ? 'Isolated cluster in your own private VPC. Ideal for development and testing.'
                            : 'Isolated cluster in your own private VPC. Production-grade node with higher compute and memory.'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {wizardError && <p className="cl-error">{wizardError}</p>}

                <button className="cl-primary-btn" onClick={handleRequestCluster} disabled={wizardBusy}>
                  {wizardBusy ? 'Requesting…' : 'Continue to Payment →'}
                </button>
              </div>
            )}

            {wizardStep === 2 && (
              <div className="cl-wizard-body">
                <div className="cl-checkout-summary">
                  <p className="cl-checkout-cluster"><code>{pendingCluster}</code></p>
                  <p className="cl-checkout-price">
                    <strong>{tierLabel(wTier)}</strong> — {tierPrice(wTier)} subscription
                  </p>
                  <p className="cl-checkout-note">
                    You'll be charged immediately for the first month. Usage charges ({perHourPrice()}) accrue
                    separately and are billed in $100 increments against your saved payment method.
                  </p>
                  <p className="cl-checkout-note">
                    Once payment completes, cluster provisioning starts automatically. This typically takes 10–20 minutes.
                  </p>
                </div>

                <div className="jm-promo">
                  <label className="jm-promo-label">Promo code (optional)</label>
                  <div className="jm-promo-row">
                    <input
                      className="jm-promo-input"
                      type="text"
                      placeholder="Enter code"
                      value={promoCode}
                      disabled={!!promoApplied || promoBusy}
                      onChange={(e) => { setPromoCode(e.target.value); setPromoError(''); }}
                      onKeyDown={(e) => { if (e.key === 'Enter' && !promoApplied) applyPromo(); }}
                    />
                    {promoApplied ? (
                      <button
                        className="jm-promo-btn jm-promo-btn--clear"
                        onClick={() => { setPromoApplied(null); setPromoCode(''); setPromoError(''); }}
                      >
                        Remove
                      </button>
                    ) : (
                      <button className="jm-promo-btn" onClick={applyPromo} disabled={promoBusy}>
                        {promoBusy ? 'Checking…' : 'Apply'}
                      </button>
                    )}
                  </div>
                  {promoError && <p className="jm-promo-err">{promoError}</p>}
                  {promoApplied && (
                    <div className="jm-promo-badge">
                      ${promoApplied.discount_dollars} off your first month
                      {promoApplied.discounted_price != null && (
                        <> — you pay <strong>${promoApplied.discounted_price}</strong> now</>
                      )}
                    </div>
                  )}
                </div>

                {wizardError && <p className="cl-error">{wizardError}</p>}

                <button className="cl-primary-btn" onClick={handleCheckout} disabled={wizardBusy}>
                  {wizardBusy ? 'Redirecting…' : '→ Pay with Stripe'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* toast notification */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: '28px', left: '50%', transform: 'translateX(-50%)',
          background: toast.type === 'success' ? '#1a2e1a' : '#2e1a1a',
          border: `1px solid ${toast.type === 'success' ? '#3fb950' : '#f85149'}`,
          color: toast.type === 'success' ? '#3fb950' : '#f85149',
          padding: '12px 24px', borderRadius: '8px', fontSize: '0.85rem',
          fontWeight: 600, zIndex: 9999, boxShadow: '0 4px 24px rgba(0,0,0,0.5)',
          letterSpacing: '0.02em', whiteSpace: 'nowrap',
        }}>
          {toast.message}
        </div>
      )}

      {/* delete confirmation modal */}
      {deleteTarget && (
        <div className="cl-modal-overlay">
          <div className="cl-modal">
            <p className="cl-modal-title">// confirm destroy</p>
            <p className="cl-modal-body">
              {(() => {
                const s = clusters.find(c => c.cluster_name === deleteTarget)?.status;
                if (s === 'active') return <>A <strong>destroy</strong> job will be triggered. The cluster infrastructure will be torn down. This cannot be undone.</>;
                if (s === 'deletion_failed') return <>A <strong>destroy</strong> job will be retried to clean up any remaining infrastructure. No additional refund will be issued.</>;
                return <>This cluster will be permanently removed.</>;
              })()}
            </p>
            <p className="cl-modal-cluster"><code>{deleteTarget}</code></p>

            {deletePreviewLoading && (
              <p className="cl-recover-loading">Calculating credit…</p>
            )}

            {!deletePreviewLoading && deletePreview && (
              (deletePreview.amount_due_cents || 0) > 0 ? (
                <div style={{ margin: '14px 0 4px', padding: '10px 14px', background: 'rgba(210,153,34,0.10)', border: '1px solid rgba(210,153,34,0.35)', borderRadius: '6px' }}>
                  <p style={{ margin: '0 0 4px', fontSize: '0.82rem', color: '#d29922', fontWeight: 600 }}>
                    Amount due: ${(deletePreview.amount_due_cents / 100).toFixed(2)}
                  </p>
                  <p style={{ margin: 0, fontSize: '0.75rem', color: '#8b949e', lineHeight: '1.5' }}>
                    Outstanding usage (${((deletePreview.outstanding_cents || 0) / 100).toFixed(2)}) exceeds your prorated refund
                    {(deletePreview.prorated_cents || 0) > 0 ? ` ($${(deletePreview.prorated_cents / 100).toFixed(2)})` : ''}.
                    {(deletePreview.due_from_credit_cents || 0) > 0 && ` $${(deletePreview.due_from_credit_cents / 100).toFixed(2)} will be deducted from your account credit`}
                    {(deletePreview.due_from_card_cents || 0) > 0 && `${(deletePreview.due_from_credit_cents || 0) > 0 ? ' and' : ''} $${(deletePreview.due_from_card_cents / 100).toFixed(2)} will be charged to your payment method`}
                    .
                  </p>
                </div>
              ) : (
                <div style={{ margin: '14px 0 4px', padding: '10px 14px', background: 'rgba(63,185,80,0.08)', border: '1px solid rgba(63,185,80,0.25)', borderRadius: '6px' }}>
                  <p style={{ margin: '0 0 4px', fontSize: '0.82rem', color: '#3fb950', fontWeight: 600 }}>
                    Credit returned: ${(((deletePreview.refund_cents ?? deletePreview.credit_cents) || 0) / 100).toFixed(2)}
                  </p>
                  <p style={{ margin: 0, fontSize: '0.75rem', color: '#8b949e', lineHeight: '1.5' }}>
                    {((deletePreview.refund_cents ?? deletePreview.credit_cents) || 0) > 0
                      ? `${deletePreview.days_remaining} days remaining in your billing period. This credit will be automatically applied to future usage charges across all your clusters.`
                      : 'No unused billing period remaining. Credit will be automatically applied to future usage charges across all your clusters.'}
                  </p>
                </div>
              )
            )}

            {deleteError && <p className="cl-error">{deleteError}</p>}
            <div className="cl-modal-actions">
              <button className="cl-modal-confirm" onClick={handleDeleteCluster} disabled={deleteBusy || deletePreviewLoading}>
                {deleteBusy ? 'Processing…' : 'Confirm Delete'}
              </button>
              <button className="cl-modal-cancel" onClick={() => { setDeleteTarget(null); setDeleteError(''); setDeletePreview(null); }} disabled={deleteBusy}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* recover modal */}
      {recoverTarget && (
        <div className="cl-modal-overlay">
          <div className="cl-modal cl-modal--recover">

            {!recoverLaunched ? (
              <>
                <p className="cl-modal-title">// cluster recover</p>

                <div className="cl-recover-warning">
                  <span className="cl-recover-warning-icon">⚠</span>
                  <p>
                    This will <strong>destroy and fully rebuild</strong> the cluster from scratch.
                    The cluster will be <strong>offline for approximately 1 hour</strong> during the process.
                    All saved ConfigMaps and active deployments will be automatically restored from backup.
                    You will receive an <strong>email notification</strong> when the recovery is complete.
                  </p>
                </div>

                <p className="cl-modal-cluster"><code>{recoverTarget}</code></p>

                {recoverSummaryLoading && (
                  <p className="cl-recover-loading">Loading backup summary…</p>
                )}

                {recoverSummary && (
                  <div className="cl-recover-summary">
                    <div className="cl-recover-col">
                      <p className="cl-recover-col-title">
                        Deployments to restore
                        <span className="cl-recover-count">{recoverSummary.deployments.length}</span>
                      </p>
                      {recoverSummary.deployments.length === 0
                        ? <p className="cl-recover-empty">none</p>
                        : recoverSummary.deployments.map(d => (
                            <div key={d} className="cl-recover-item">
                              <span className="cl-recover-bullet">▸</span>{d}
                            </div>
                          ))
                      }
                    </div>
                    <div className="cl-recover-col">
                      <p className="cl-recover-col-title">
                        ConfigMaps to restore
                        <span className="cl-recover-count">{recoverSummary.configmaps.length}</span>
                      </p>
                      {recoverSummary.configmaps.length === 0
                        ? <p className="cl-recover-empty">none</p>
                        : recoverSummary.configmaps.map(c => (
                            <div key={c} className="cl-recover-item">
                              <span className="cl-recover-bullet">▸</span>{c}
                            </div>
                          ))
                      }
                    </div>
                  </div>
                )}

                {recoverError && <p className="cl-error">{recoverError}</p>}

                <div className="cl-modal-actions">
                  <button
                    className="cl-modal-confirm cl-modal-confirm--recover"
                    onClick={handleStartRecover}
                    disabled={recoverBusy || recoverSummaryLoading}
                  >
                    {recoverBusy ? 'Launching…' : '↺ Start Rebuilding'}
                  </button>
                  <button className="cl-modal-cancel" onClick={closeRecoverModal} disabled={recoverBusy}>
                    Cancel
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="cl-modal-title">// recovery launched</p>
                <p className="cl-modal-body">
                  The recover job has been queued. The cluster will be destroyed and rebuilt automatically.
                  Check back in ~1 hour — <strong>you will receive an email</strong> when recovery is complete.
                </p>
                <p className="cl-modal-cluster"><code>{recoverTarget}</code></p>
                <div className="cl-modal-actions">
                  <button className="cl-modal-confirm" onClick={closeRecoverModal}>Close</button>
                </div>
              </>
            )}

          </div>
        </div>
      )}


      {/* instruction panel */}
      <div className="cl-guide">
        <p className="cl-guide-title">cluster ops</p>
        <p className="cl-guide-section">isolation</p>
        <p className="cl-guide-text">
          Every cluster runs in your account's <strong>own private VPC</strong> on AWS. Your network, nodes,
          compute, and storage are fully isolated — no shared infrastructure with other accounts.
          Real infrastructure isolation, not namespace partitioning.
        </p>
        <p className="cl-guide-section">01 / request</p>
        <p className="cl-guide-text">Choose a name and tier. Saved as <em>Pending</em> until payment clears.</p>
        <p className="cl-guide-section">02 / payment</p>
        <p className="cl-guide-text">Monthly subscription covers the base node. Card is saved per cluster for usage charges.</p>
        <p className="cl-guide-section">03 / provision</p>
        <p className="cl-guide-text">Once payment clears and status shows Approved, click <strong>Start Cluster</strong> to begin provisioning. Status goes: Approved → Building → Active.</p>
        <p className="cl-guide-section">04 / usage</p>
        <p className="cl-guide-text">
          Deployments accrue {perHourPrice()} per replica/hr.
          Balance auto-charges at $100 increments to the cluster's saved card.
        </p>
        <p className="cl-guide-section">tiers</p>
        <p className="cl-guide-text">GOLD — {tierPrice('dev')} — dedicated node, dev workloads.</p>
        <p className="cl-guide-text">PREMIUM — {tierPrice('prod')} — dedicated node, production traffic.</p>
      </div>
    </div>
  );
}
